import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App.js'
import { ApiRequestError } from './lib/api-client.js'
import './styles.css'

/**
 * Настройки запросов заданы один раз здесь, а не на каждом экране.
 *
 * Главное из них — правило повторов. Повторять 4xx не нужно: это ответ
 * «данные не годятся» или «доступа нет», и от второй попытки он не
 * изменится, а человек будет ждать. А вот сбой сети или 5xx стоит
 * попробовать ещё раз: чаще всего это моргнувшее соединение, и незаметный
 * повтор лучше, чем панель ошибки.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
          return false
        }

        return failureCount < 2
      },
      staleTime: 10_000,
      /**
       * Обновление при возврате к вкладке включено сознательно: админку
       * держат открытой часами, и данные за это время устаревают. Человек,
       * вернувшийся к вкладке, должен видеть текущее состояние, а не то,
       * что было утром.
       */
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Изменяющие запросы не повторяются автоматически никогда:
      // повтор действия — это решение человека, а не библиотеки
      retry: false,
    },
  },
})

const root = document.getElementById('root')

if (!root) {
  throw new Error('Не найден элемент #root')
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
)
